import React, { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
    Container,
    Card,
    CardBody,
    CardHeader,
    Spinner,
    Alert,
    Nav,
    NavItem,
    NavLink,
    Row,
    Col,
} from "reactstrap";
import classnames from "classnames";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Hooks
import { useSettingsAuth } from "../hooks/useSettingsAuth";

// Components
import CompanySettingsTab from "../components/CompanySettingsTab";
import PaymentSettingsTab from "../components/PaymentSettingsTab";
import AccountingSettingsTab from "../components/AccountingSettingsTab";
import InstallmentGroupsTab from "../components/InstallmentGroupsTab";
import UserProfileTab from "../components/UserProfileTab"
import HolidayCalendarTab from "../components/HolidayCalendarTab";
import AuditLogTable from "../../../pages/Tables/AuditLogTable";
import "./SettingsPage.scss";

// Theme Colors
const NAVY = "#0A316E"; // İstenilen Koyu Lacivert
const LIGHT_BG = "#f6f7fb"; // Arka Plan

export default function SettingsPage() {
    document.title = "Ayarlar | Tahsilat360";

    const {
        companyId,
        companyCode,
        pageLoading,
        canViewAll,
        isAccountant,
        isSuperAdmin,
    } = useSettingsAuth();

    // Kullanıcı rolüne göre menü listesini dinamik oluştur
    const tabsList = useMemo(() => {
        const fullList = [
            {
                id: "company",
                label: "Firma Bilgileri",
                description: "Firma profilinizi, iletişim bilgilerinizi ve görünüm ayarlarınızı yönetin.",
                icon: "bx bx-buildings",
                accent: "#0A316E",
                roles: ["ALL"],
            },
            {
                id: "password",
                label: "Kullanıcı Bilgileri",
                description: "Profil bilgilerinizi ve hesap güvenliği tercihlerinizi güncelleyin.",
                icon: "bx bx-lock-alt",
                accent: "#c2410c",
                roles: ["USER", "ACCOUNTANT", "ALL"],
            },
            {
                id: "payment",
                label: "Sanal POS Entegrasyon",
                description: "Sanal POS sağlayıcılarınızı bağlayın ve ödeme ayarlarını düzenleyin.",
                icon: "bx bx-credit-card",
                accent: "#2563eb",
                roles: ["ALL"],
            },
            {
                id: "accounting",
                label: "Muhasebe Entegrasyonu",
                description: "Muhasebe uygulamalarınızın bağlantı ve aktarım ayarlarını yapılandırın.",
                icon: "bx bx-book-open",
                accent: "#0f766e",
                roles: ["ALL"],
            },
            {
                id: "groups",
                label: "Taksit Grupları",
                description: "Cari gruplarınıza özel taksit seçeneklerini ve oranlarını belirleyin.",
                icon: "bx bx-layer",
                accent: "#7c3aed",
                roles: ["ACCOUNTANT", "ALL"],
            },
        ];

        // Denetim İzi yalnız süper admin ve firma yöneticisine açıktır (canViewAll); muhasebe/kasa
        // rolleri finansal listeleri görür ama "kim ne yaptı" kaydını görmez. Sınır backend'de.
        fullList.push({
            id: "audit",
            label: "Denetim İzi",
            description: "Para hareketi, yetki ve kimlik ile ilgili işlemlerin kaydını inceleyin.",
            icon: "bx bx-shield-quarter",
            accent: "#475569",
            roles: ["ALL"],
        });

        // Resmî tatil takvimi ulusaldır, firmaya ait değildir: yalnız süper admin görür.
        if (isSuperAdmin) {
            fullList.push({
                id: "holidays",
                label: "Resmî Tatil Takvimi",
                description: "Valör hesaplamalarında kullanılan resmî tatil günlerini yönetin.",
                icon: "bx bx-calendar-event",
                accent: "#be123c",
                roles: ["ALL"],
            });
        }

        if (canViewAll) {
            return fullList;
        }
        if (isAccountant) {
            return fullList.filter(t => ["ACCOUNTANT", "USER"].includes(t.roles[0]) || t.roles.includes("ACCOUNTANT"));
        }
        // Standart kullanıcı veya yetkisiz
        return fullList.filter((tab) => tab.id === "password");
    }, [canViewAll, isAccountant, isSuperAdmin]);

    // Seçili sekme URL'de tutulur (?tab=...). Yalnız local state ile tutulduğunda,
    // zaten /settings üzerindeyken profil menüsünden gelen yönlendirme bileşeni
    // remount etmediği için sekmeyi değiştirmiyordu. Bilinmeyen ya da yetki
    // dışındaki sekme adı kart menüsüne düşer.
    const [searchParams, setSearchParams] = useSearchParams();
    const requestedTab = searchParams.get("tab");
    // Döviz kuru ve sözleşmeler bu sürümde Firma Bilgileri'nin alt sekmesi; aramadan gelen
    // ?tab=currency / ?tab=contracts adresi o alt sekmeyi açar.
    const companySection = ["currency", "contracts"].includes(requestedTab) ? requestedTab : "company";
    const resolvedTab = companySection === "company" ? requestedTab : "company";
    const activeTab = tabsList.some((t) => t.id === resolvedTab) ? resolvedTab : null;

    const setActiveTab = (tabId) => {
        const next = new URLSearchParams(searchParams);
        if (tabId) next.set("tab", tabId);
        else next.delete("tab");
        setSearchParams(next, { replace: true });
    };

    const activeTabInfo = tabsList.find((tab) => tab.id === activeTab);
    // Yedi kartta üçlü dizilim son satırda tek kart bırakıyor; dörtlü dizilimle 4 + 3 olur.
    const cardColumns = tabsList.length > 6 ? "3" : "4";

    if (pageLoading) {
        return (
            <div className="page-content text-center p-5" style={{ background: LIGHT_BG, minHeight: "100vh" }}>
                <Spinner color="primary" />
                <div className="mt-3 text-muted">Kullanıcı bilgileri yükleniyor...</div>
            </div>
        );
    }

    if (!companyId) {
        return (
            <div className="page-content p-5" style={{ background: LIGHT_BG, minHeight: "100vh" }}>
                <Container>
                    <Alert color="danger" className="shadow-sm border-0">
                        <i className="bx bx-error-alt me-2"></i>
                        Firma bilgisi bulunamadı. Lütfen tekrar giriş yapın.
                    </Alert>
                </Container>
            </div>
        );
    }

    const renderActiveTab = () => {
        switch (activeTab) {
            case "company":
                return canViewAll && <CompanySettingsTab key={companySection} companyId={companyId} section={companySection} />;
            case "payment":
                return canViewAll && (
                    <PaymentSettingsTab companyId={companyId} companyCode={companyCode} />
                );
            case "accounting":
                return canViewAll && <AccountingSettingsTab companyId={companyId} />;
            case "groups":
                // V125: taksit grubu artık paket özelliği değil, paket limiti — ekran
                // her firmaya açık, tavan grup EKLERKEN kontrol ediliyor.
                return (canViewAll || isAccountant) && (
                    /* Aktif POS oranı çekme ve grup silme uçları yalnız
                       SUPER_ADMIN/COMPANY_ADMIN'e açık; muhasebeci yetkisi
                       burada taşınıyor ki ekran çalışmayan buton göstermesin. */
                    <InstallmentGroupsTab
                        companyId={companyId}
                        canManage={canViewAll}
                        /* Abonelik taksit grubu anahtarı platformun kendi tahsilatını
                           ilgilendirdiği için yalnız süper adminde görünür. */
                        isSuperAdmin={isSuperAdmin}
                    />
                );
            case "audit":
                return canViewAll && <AuditLogTable embedded />;
            case "holidays":
                return isSuperAdmin && <HolidayCalendarTab />;
            case "password":
                return <UserProfileTab />;
            default:
                return null;
        }
    };

    return (
        <div className="page-content" style={{ background: LIGHT_BG, minHeight: "100vh", paddingBottom: "3rem" }}>
            <Container fluid className="py-4">

                {/* Üst Başlık ve Breadcrumb */}
                <div className="d-flex justify-content-between align-items-center mb-4" data-tour="settings-header">
                    <div>
                        <div className="text-muted small d-flex align-items-center mb-1">
                            <span className="fw-bold" style={{ color: NAVY }}>Yönetim</span>
                            <i className="bx bx-chevron-right mx-1" style={{ fontSize: '0.8rem' }}></i>
                            {activeTab ? (
                                <button
                                    type="button"
                                    className="settings-breadcrumb-button fw-semibold"
                                    onClick={() => setActiveTab(null)}
                                >
                                    Ayarlar
                                </button>
                            ) : (
                                <span className="fw-semibold">Ayarlar</span>
                            )}
                        </div>
                        <h4 className="fw-bold mb-0" style={{ color: NAVY }}>
                            {activeTabInfo?.label || "Ayarlar"}
                        </h4>
                    </div>
                </div>

                {!activeTab ? (
                    <div className="settings-menu" data-tour="settings-sidebar">
                        <div className="text-center mb-4">
                            <h5 className="fw-bold mb-2" style={{ color: NAVY }}>Yapılandırmak istediğiniz alanı seçin</h5>
                            <p className="text-muted mb-0">Tüm firma ve kullanıcı ayarlarınıza buradan ulaşabilirsiniz.</p>
                        </div>

                        <Row className="g-4 justify-content-center">
                            {tabsList.map((tab) => (
                                <Col key={tab.id} xs="12" md="6" xl={cardColumns}>
                                    <Card
                                        role="button"
                                        tabIndex="0"
                                        className="settings-menu-card h-100 w-100 text-start border-0 rounded-4"
                                        onClick={() => setActiveTab(tab.id)}
                                        onKeyDown={(event) => {
                                            if (event.key === "Enter" || event.key === " ") {
                                                event.preventDefault();
                                                setActiveTab(tab.id);
                                            }
                                        }}
                                        style={{ "--settings-accent": tab.accent }}
                                        aria-label={`${tab.label} ayarlarını aç`}
                                    >
                                        <CardBody className="p-4 d-flex flex-column">
                                            <div className="d-flex align-items-start justify-content-between mb-4">
                                                <div className="settings-menu-card__icon d-flex align-items-center justify-content-center">
                                                    <i className={`${tab.icon} fs-3`}></i>
                                                </div>
                                                <i className="bx bx-right-arrow-alt settings-menu-card__arrow"></i>
                                            </div>
                                            <h5 className="fw-bold mb-2" style={{ color: NAVY }}>{tab.label}</h5>
                                            <p className="text-muted small mb-0 lh-base">{tab.description}</p>
                                        </CardBody>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </div>
                ) : (
                    <>
                        {/* AYAR DETAYLARINDA YATAY ÜST MENÜ (mobilde yatay kaydırılır) */}
                        <Card className="shadow-sm border-0 rounded-4 mb-4" data-tour="settings-sidebar">
                            <CardBody className="p-2">
                                <Nav pills className="flex-nowrap overflow-auto gap-2" style={{ scrollbarWidth: "none" }}>
                                    {tabsList.map((t) => {
                                        const isActive = activeTab === t.id;
                                        return (
                                            <NavItem key={t.id}>
                                                <NavLink
                                                    className={classnames(
                                                        "rounded-pill px-3 py-2 d-flex align-items-center gap-2 border",
                                                        { active: isActive }
                                                    )}
                                                    style={{
                                                        whiteSpace: "nowrap",
                                                        cursor: "pointer",
                                                        backgroundColor: isActive ? NAVY : "#fff",
                                                        color: isActive ? "white" : "#555",
                                                        borderColor: isActive ? NAVY : "#eee",
                                                        transition: "all 0.2s",
                                                    }}
                                                    onClick={() => setActiveTab(t.id)}
                                                >
                                                    <i className={`${t.icon} fs-6`}></i>
                                                    <span className="small fw-semibold">{t.label}</span>
                                                </NavLink>
                                            </NavItem>
                                        );
                                    })}
                                </Nav>
                            </CardBody>
                        </Card>

                        {/* İÇERİK ALANI (tam genişlik) */}
                        <Card className="shadow-lg border-0 rounded-4" style={{ minHeight: "500px" }} data-tour="settings-card">
                            <CardHeader className="bg-white border-bottom border-light rounded-top-4 p-4 d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center gap-3">
                                    <div
                                        className="rounded-circle d-flex align-items-center justify-content-center shadow-sm"
                                        style={{
                                            width: 48,
                                            height: 48,
                                            background: `linear-gradient(135deg, ${NAVY}, #1a4d8c)`,
                                            color: "#fff",
                                        }}
                                    >
                                        <i className={`${activeTabInfo?.icon} fs-4`}></i>
                                    </div>

                                    <div>
                                        <h4 className="mb-0 fw-bold" style={{ color: NAVY }}>
                                            {activeTab === "payment"
                                                ? "Sanal POS Entegrasyonları"
                                                : activeTabInfo?.label}
                                        </h4>
                                        {companyCode && (
                                            <small className="text-muted d-block mt-1">
                                                Firma Kodu: <span className="font-monospace fw-bold">{companyCode}</span>
                                            </small>
                                        )}
                                    </div>
                                </div>
                            </CardHeader>

                            <CardBody className="p-4">
                                {renderActiveTab()}
                            </CardBody>
                        </Card>
                    </>
                )}

                <ToastContainer position="top-right" autoClose={3000} />
            </Container>
        </div>
    );
}
